/**
 * Caps and fail-soft helpers so the desk Mapbox WebView cannot grow without bound.
 * Track / dest / OFP geometry and JS-heap pressure — not a second persist schema.
 */

export const DEST_OVERLAY_MAX = 500;
export const MAP_TRACK_DISPLAY_MAX = 160;
export const MAP_ROUTE_MAX_POINTS = 96;
export const FLIGHT_TRACKS_MAX = 32;
export const LAST_KNOWN_STORE_MIN_MS = 8_000;
export const LAST_KNOWN_STORE_MIN_NM = 0.5;
export const MEMORY_PRESSURE_RATIO = 0.78;
export const MEMORY_CRITICAL_RATIO = 0.88;
export const PERSIST_DEBOUNCE_MS = 2_000;
export const TELEMETRY_STORE_MIN_MS = 5_000;

export type MemoryPressureLevel = "ok" | "high" | "critical";

export type JsHeapMemory = {
  usedBytes: number;
  limitBytes: number;
};

/** Evenly keep first + last. Empty / already-short arrays are unchanged. */
export function capPolylinePoints<T>(points: readonly T[], max: number): T[] {
  if (!Array.isArray(points) || points.length <= max) return points.slice();
  if (max <= 0) return [];
  if (max === 1) return points.slice(-1);
  const last = points.length - 1;
  const out: T[] = [];
  for (let i = 0; i < max; i += 1) {
    const idx = i === max - 1 ? last : Math.round((i * last) / (max - 1));
    const pt = points[idx];
    if (pt !== undefined && out[out.length - 1] !== pt) out.push(pt);
  }
  return out;
}

export function memoryPressureLevel(usedBytes: number, limitBytes: number): MemoryPressureLevel {
  if (!Number.isFinite(usedBytes) || !Number.isFinite(limitBytes) || limitBytes <= 0) return "ok";
  const ratio = usedBytes / limitBytes;
  if (ratio >= MEMORY_CRITICAL_RATIO) return "critical";
  if (ratio >= MEMORY_PRESSURE_RATIO) return "high";
  return "ok";
}

export function readJsHeapMemory(): JsHeapMemory | null {
  const perf = globalThis.performance as
    | { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } }
    | undefined;
  const used = Number(perf?.memory?.usedJSHeapSize);
  const limit = Number(perf?.memory?.jsHeapSizeLimit);
  if (!Number.isFinite(used) || !Number.isFinite(limit) || limit <= 0) return null;
  return { usedBytes: used, limitBytes: limit };
}

export function readMemoryPressureLevel(): MemoryPressureLevel {
  const heap = readJsHeapMemory();
  return heap ? memoryPressureLevel(heap.usedBytes, heap.limitBytes) : "ok";
}

/**
 * Brief heap spikes during pan must not rebuild / drop overlays.
 * Need two consecutive non-ok samples to leave ok; critical steps down
 * through high before returning to ok.
 */
export function settleMemoryPressureLevel(
  previous: MemoryPressureLevel,
  sample: MemoryPressureLevel,
  failStreak: number,
): { level: MemoryPressureLevel; failStreak: number } {
  if (sample === "ok") {
    if (previous === "critical") return { level: "high", failStreak: 0 };
    return { level: "ok", failStreak: 0 };
  }
  const streak = failStreak + 1;
  if (previous === "ok" && streak < 2) {
    return { level: "ok", failStreak: streak };
  }
  return { level: sample, failStreak: streak };
}

export function mapTrackDisplayMax(level: MemoryPressureLevel = "ok"): number {
  if (level === "critical") return 40;
  if (level === "high") return 80;
  return MAP_TRACK_DISPLAY_MAX;
}

export function destOverlayBudget(level: MemoryPressureLevel = "ok"): number {
  if (level === "critical") return 0;
  if (level === "high") return 80;
  return DEST_OVERLAY_MAX;
}

export function mapTrackDisplayPoints<T>(points: readonly T[], level: MemoryPressureLevel = "ok"): T[] {
  return capPolylinePoints(points, mapTrackDisplayMax(level));
}
